/**
 * Video timeline (spec §47-48). Static and video creative share the same
 * campaign, approval, publishing and audit lifecycle (spec §47); the timeline
 * is what a video Creative Spec renders against. It is a JSON document, same as
 * a static template: scenes with sources and motion, overlays for the
 * deterministic titles/logo/CTA/end card, and audio tracks. An AI-generated
 * clip is just a scene whose source is AI_VIDEO, so AI video plugs into the
 * same timeline later without changing the campaign domain (spec §100).
 */

import { z } from 'zod';

export const SCENE_SOURCES = ['IMAGE', 'VIDEO', 'AI_VIDEO', 'GENERATED_GRAPHIC'] as const;
export const SceneSourceSchema = z.enum(SCENE_SOURCES);
export type SceneSource = z.infer<typeof SceneSourceSchema>;

export const MOTION_TYPES = ['NONE', 'PAN', 'ZOOM', 'PAN_ZOOM'] as const;

export const VideoSceneSpecSchema = z
  .object({
    id: z.string().min(1),
    startMs: z.number().int().nonnegative(),
    durationMs: z.number().int().positive(),
    source: SceneSourceSchema,
    sourceAssetId: z.string().min(1).optional(),
    transitionIn: z.string().optional(),
    transitionOut: z.string().optional(),
    motion: z
      .object({ type: z.enum(MOTION_TYPES), intensity: z.number().min(0).max(1).optional() })
      .strict()
      .optional(),
  })
  .strict();
export type VideoSceneSpec = z.infer<typeof VideoSceneSpecSchema>;

export const AUDIO_ROLES = ['MUSIC', 'ORIGINAL', 'VOICEOVER', 'SFX'] as const;

export const AudioTrackSpecSchema = z
  .object({
    id: z.string().min(1),
    role: z.enum(AUDIO_ROLES),
    sourceAssetId: z.string().min(1).optional(),
    startMs: z.number().int().nonnegative().optional(),
    gain: z.number().optional(),
  })
  .strict();
export type AudioTrackSpec = z.infer<typeof AudioTrackSpecSchema>;

/** A deterministic overlay - the titles, logo, URL, captions, end card that
 *  HipNumber renders on top of the scenes (spec §47, AI + Motion Template). */
export const VideoOverlaySpecSchema = z
  .object({
    id: z.string().min(1),
    startMs: z.number().int().nonnegative(),
    durationMs: z.number().int().positive(),
    role: z.string(),
    locked: z.boolean().optional(),
  })
  .strict();
export type VideoOverlaySpec = z.infer<typeof VideoOverlaySpecSchema>;

export const VideoTimelineSpecSchema = z
  .object({
    id: z.string().min(1),
    durationMs: z.number().int().positive(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    fps: z.number().int().positive(),
    scenes: z.array(VideoSceneSpecSchema).min(1),
    audio: z.array(AudioTrackSpecSchema).optional(),
    overlays: z.array(VideoOverlaySpecSchema).optional(),
  })
  .strict();
export type VideoTimelineSpec = z.infer<typeof VideoTimelineSpecSchema>;
